
import img0 from './Images/App_Role_Configuration.png';
import img1 from './Images/Data_Access_Role_Configuration.png';
  
const content = `# Assigning Roles in Microsoft Entra ID

OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization. Both the OnData application roles and the 
data access roles can be configured in your IAM system, so that you can use your IAM system to control both user's access to the OnData application and user's access to 
your sensitive data. For details regarding the OnData application roles, please refer to [**OnData Application Roles**](reference_doc#ondata_portal/ondata_application_roles). For details on how 
to configure data access roles, please refer to [**Data Access Roles**](reference_doc#ondata_portal/data_access_roles). The purpose of this document is to detail the process of assigning 
application roles and data access roles to users and user groups in Microsoft Entra ID (formerly Azure Active Directory) as an example. The process to configure 
such roles within other types of IAM systems can be similar.  

## Prerequisite
a. An Entra ID user account with at least one of the following roles: Global Administrator, Cloud Application Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  
c. The OnData Portal has been integrated with your Microsoft Entra ID for Single Sign-On (SSO) purpose. For details regarding Entra ID integration, please refer to 
[**Entra ID Integration Using OIDC**](reference_doc#ondata_portal/sso_integration/entraid_integration_using_oidc) or [**Entra ID Integration Using SAML**](reference_doc#ondata_portal/sso_integration/entraid_integration_using_saml) .  

## Configure App Roles for OnData Application
a. Login to the Azure Portal and go to Microsoft Entra ID. Select "Manage" -> "App registrations"  
b. From the list of applications, select the "OnData Portal" application  
c. Select "Manage" -> "App roles" from the left menu  
d. For each application role configured at the OnData Portal, including the built-in roles and custom roles, if such role is needed for your organization, follow the 
steps below to add that role to the list:
   * Click on "Create app role"  
   * Enter a "Display name", select the "Users/Groups" option under the "Allowed member types", and enter the role value that is the same as the role value configured 
   at the OnData Portal. Below is the list of default roles configured at the OnData Portal:  
  
| Display Name | Role Value | Description |
| ------------ | ---------- | ----------- |
| System Administrator | SystemAdministrator | A system administrator has full access to all features. |
| Data Analyst | DataAnalyst | A data analyst manages data catalog, data ownership and data encryption/obfuscation rules. |
| Data Management Officer | DataManagementOfficer | A data management officer manages data classifications, data access roles, data ownership, data catalog and data encryption/obfuscation rules. |
| Database Administrator | DatabaseAdministrator | A database administrator manages database related operations. |
| SecureFile Only | SecureFileOnly | A limited user role which restricts it's users to only have access to the SecureFile desktop app and user profile page on Portal. |
| Security Administrator | SecurityAdministrator | A security administrator manages the network and security related features. |
| User | User | A generic user role with no special permission to the system. |

   * Enter a description and select the "Apply" button to create the application role  
  
![App Role Configuration](${ img0 })  
    
## Configure Data Access Roles
a. Login to the Azure Portal and go to Microsoft Entra ID. Select "Manage" -> "App registrations"  
b. From the list of applications, select the "OnData Portal" application  
c. Select "Manage" -> "App roles" from the left menu  
d. For each data access role configured at the OnData Portal, if it is needed for your organization, follow the steps below to add that data access role to the list:
   * Click on "Create app role"  
   * Enter a "Display name" with a prefix of "DataAccessRole_", select the "Users/Groups" option under the "Allowed member types", and enter the role value 
   that is the same as the role value configured at the OnData Portal, except that it is prefixed with "DataAccessRole_". For example, if the data access role at the 
   OnData Portal has a value of "DataAdmin", the value to be used here is "DataAccessRole_DataAdmin"  
   * Enter a description and select the "Apply" button to create the data access role  
  
![Data Access Role Configuration](${ img1 })  
  
## Assign OnData Roles and Data Access Roles to Users and User Groups
Once the OnData application roles and the data access roles are configured through the App registration process in Entra ID, those roles can be assigned to your users 
and user groups. Below are the steps:  
a. Go to Azure Portal, and select "Enterprise applications"  
b. From the list of applications, select the "OnData Portal" application  
c. On the “Overview” page, select “Assign users and groups” under the tile named “1.  Assign users and groups”  
d. Click on "Add user/group", and select one or more users or user groups from Entra ID  
e. Select an OnData application role or a data access role  
f. Click on the "Assign" button to complete the assignment  
g. Repeat the process above to assign various roles to various groups of users  
  
A user can be assigned one or more application roles and data access roles. When multiple roles are assigned, the permission of that user will be the combined list of 
permissions from all assigned roles.  
  
  `
  
export default content;
